<script setup lang="ts">
import { roles } from '~/enums'
import { useCurrentRoleStore } from '~/stores/currentRole'

type NavigationItem = {
  label: string
  to: string
  icon: string
}

const route = useRoute()
const user = useSupabaseUser()
const currentRoleStore = useCurrentRoleStore()
const { signOut: completeSignOut } = useSignOut()
const { currentRole } = storeToRefs(currentRoleStore)
useSyncCurrentRole()
const { memberships: accessibleSchools, isSystemAdmin } = useSchoolMemberships()
async function handleSchoolChange(event: Event) {
  const school = accessibleSchools.value.find(
    (item) => item.schoolId === (event.target as HTMLSelectElement).value,
  )
  if (!school) return
  menuOpen.value = false
  if (school.schoolId === currentRole.value.schoolId) {
    await navigateTo('/dashboard/admin')
    return
  }
  currentRoleStore.setCurrentStudent(null)
  currentRoleStore.setCurrentGrade('')
  useSchoolUsersStore().deleteSchoolUsers()
  clearNuxtData()
  currentRoleStore.setCurrentRole(school)
  await navigateTo('/dashboard/admin')
}
const { isSoundEnabled, toggleSound } = useSound()
const { teacherClasses, selectedClassId, selectClass } = useTeacherDemo()
const menuOpen = ref(false)
const isScrolled = ref(false)
const config = useRuntimeConfig()
const { $pwa } = useNuxtApp()
type UpdateCheckStatus = 'idle' | 'checking' | 'current' | 'error' | 'updated'

const updateCheckStatus = ref<UpdateCheckStatus>('idle')
const appVersion = computed(() => String(config.public.appVersion))
let updateStatusTimer: number | undefined

const showClassSwitcher = computed(
  () =>
    route.path.startsWith('/dashboard/teacher') ||
    route.path.startsWith('/dashboard/admin/teacher/'),
)
const showSchoolIdentity = computed(
  () =>
    Boolean(user.value) &&
    !route.path.startsWith('/classes') &&
    !route.path.startsWith('/adventure'),
)

const isVisible = computed(
  () =>
    !route.path.startsWith('/proposals/') &&
    !route.path.endsWith('/preview') &&
    ![
      '/login',
      '/forgot-password',
      '/reset-password',
      '/auth/confirm',
      '/invitations/accept',
      '/offline',
    ].includes(route.path),
)
const appItems = computed<NavigationItem[]>(() => {
  const role = currentRole.value?.role
  if (
    role === roles.GUARDIAN ||
    role === roles.ADMIN ||
    role === roles.SYSTEM_ADMIN
  ) {
    return []
  }
  if (role === roles.TEACHER) {
    return [
      { label: 'Classes', to: '/classes', icon: 'i-lucide-book-open' },
      { label: 'Adventure', to: '/adventure', icon: 'i-lucide-map' },
    ]
  }
  return [
    { label: 'Home', to: '/', icon: 'i-lucide-house' },
    { label: 'Classes', to: '/classes', icon: 'i-lucide-book-open' },
    { label: 'Adventure', to: '/adventure', icon: 'i-lucide-map' },
  ]
})
const dashboardTitle = computed(() =>
  currentRole.value?.role === roles.GUARDIAN ? 'Family space' : 'My dashboard',
)
const schoolName = computed(
  () => currentRole.value?.schoolName?.trim() || 'WLearn Academy',
)
const schoolHome = computed(() => {
  switch (currentRole.value?.role) {
    case roles.ADMIN:
      return '/dashboard/admin'
    case roles.TEACHER:
      return '/dashboard/teacher'
    case roles.GUARDIAN:
      return '/dashboard/parent'
    case roles.STUDENT:
      return '/dashboard/student'
    default:
      return '/'
  }
})
const userDisplayName = computed(() => {
  const metadata = user.value?.user_metadata as
    | Record<string, unknown>
    | undefined
  const fullName = [metadata?.full_name, metadata?.fullName].find(
    (value) => typeof value === 'string' && value.trim(),
  )
  if (typeof fullName === 'string') return fullName.trim()

  const name = [
    metadata?.firstName ?? metadata?.first_name,
    metadata?.lastName ?? metadata?.last_name,
  ]
    .filter(
      (value): value is string =>
        typeof value === 'string' && Boolean(value.trim()),
    )
    .join(' ')

  return name || user.value?.email?.split('@')[0] || 'Account'
})
const userInitial = computed(() =>
  userDisplayName.value.charAt(0).toUpperCase(),
)
const canManageProposals = computed(() => isSystemAdmin.value)
const dashboardItems = computed<NavigationItem[]>(() => {
  switch (currentRole.value?.role) {
    case roles.STUDENT:
      return [
        {
          label: 'Homework',
          to: '/dashboard/student/assignments',
          icon: 'i-lucide-clipboard-check',
        },
        {
          label: 'Achievements',
          to: '/dashboard/student/achievements',
          icon: 'i-lucide-trophy',
        },
      ]
    case roles.GUARDIAN:
      return [
        {
          label: 'Progress',
          to: '/dashboard/parent/progress',
          icon: 'i-lucide-chart-line',
        },
        {
          label: 'Homework',
          to: '/dashboard/parent/homework',
          icon: 'i-lucide-clipboard-check',
        },
      ]
    case roles.TEACHER:
      return [
        {
          label: 'Overview',
          to: '/dashboard/teacher',
          icon: 'i-lucide-house',
        },
        {
          label: 'Students',
          to: '/dashboard/teacher/students',
          icon: 'i-lucide-users-round',
        },
        {
          label: 'Curriculum',
          to: '/dashboard/teacher/curriculum',
          icon: 'i-lucide-gamepad-2',
        },
        {
          label: 'Reports',
          to: '/dashboard/teacher/reports',
          icon: 'i-lucide-chart-no-axes-combined',
        },
        {
          label: 'Assignments',
          to: '/dashboard/teacher/assignments',
          icon: 'i-lucide-clipboard-check',
        },
      ]
    case roles.ADMIN:
      return [
        {
          label: 'Dashboard',
          to: '/dashboard/admin',
          icon: 'i-lucide-house',
        },
        {
          label: 'Analytics',
          to: '/dashboard/admin/analytics',
          icon: 'i-lucide-chart-no-axes-combined',
        },
        {
          label: 'People',
          to: '/dashboard/admin/community',
          icon: 'i-lucide-users-round',
        },
        {
          label: 'Curriculum',
          to: '/dashboard/admin/curriculum',
          icon: 'i-lucide-gamepad-2',
        },
        {
          label: 'Billing',
          to: '/dashboard/admin/billing',
          icon: 'i-lucide-credit-card',
        },
      ]
    case roles.SYSTEM_ADMIN:
      return [
        {
          label: 'Dashboard',
          to: '/dashboard',
          icon: 'i-lucide-house',
        },
      ]
    default:
      return []
  }
})

function classGameReturnPath(subject: string, game: string) {
  const returnTo = route.query.returnTo?.toString()
  if (returnTo?.startsWith('/dashboard/')) return returnTo

  // Published classroom activities are UUIDs, not catalogue/YAML game ids.
  // Their level-selection route belongs to the retired game-progress model;
  // return to the class's current-week lesson list instead.
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      game,
    )
  ) {
    return `/classes/${subject}`
  }

  const query = new URLSearchParams()
  const grade = route.query.grade?.toString()
  const term = route.query.term?.toString()
  if (grade) query.set('grade', grade)
  if (term) query.set('term', term)

  const queryString = query.toString()
  return `/classes/${subject}/${game}${queryString ? `?${queryString}` : ''}${route.hash}`
}
const leadingAction = computed(() => {
  const segments = route.path.split('/').filter(Boolean)

  if (segments[0] === 'adventure' && segments[1]) {
    const overviewPath = `/adventure/${segments[1]}`
    if (segments[2] === 'game') {
      return { label: 'Close game', icon: 'i-lucide-x', to: overviewPath }
    }
    if (segments[2] === 'mission') {
      return {
        label: 'Close adventure level',
        icon: 'i-lucide-x',
        to: overviewPath,
      }
    }
    if (segments[2] === 'complete') {
      return {
        label: 'Back to adventure map',
        icon: 'i-lucide-arrow-left',
        to: overviewPath,
      }
    }
    if (segments.length === 2) {
      return {
        label: 'Close adventure levels',
        icon: 'i-lucide-x',
        to: '/adventure',
      }
    }
    if (segments[2] === 'story') {
      return {
        label: 'All adventures',
        icon: 'i-lucide-arrow-left',
        to: '/adventure',
      }
    }
  }

  if (segments[0] === 'classes' && segments[1]) {
    if (segments[3] === 'game') {
      return {
        label: 'Close game',
        icon: 'i-lucide-x',
        to: classGameReturnPath(segments[1]!, segments[2]!),
      }
    }
    return { label: 'All classes', icon: 'i-lucide-arrow-left', to: '/classes' }
  }

  return undefined
})

function isActive(item: NavigationItem) {
  if (item.to === '/') return route.path === item.to
  if (
    ['/dashboard/admin', '/dashboard/teacher', '/dashboard/student'].includes(
      item.to,
    )
  ) {
    return route.path === item.to
  }
  return route.path === item.to || route.path.startsWith(`${item.to}/`)
}

async function signOut() {
  try {
    await completeSignOut()
  } catch (error) {
    useToast().add({
      title: 'Unable to sign out',
      description: 'Please try again.',
      color: 'error',
    })
    console.error(error)
  }
}

async function checkForUpdate() {
  if ($pwa?.needRefresh) {
    updateCheckStatus.value = 'checking'
    sessionStorage.setItem('pwa-update-just-installed', 'true')

    try {
      await $pwa.updateServiceWorker()
    } catch {
      sessionStorage.removeItem('pwa-update-just-installed')
      showUpdateStatus('error')
    }
    return
  }

  if (!('serviceWorker' in navigator) || !navigator.onLine) {
    showUpdateStatus('error')
    return
  }

  updateCheckStatus.value = 'checking'

  try {
    const registration = await navigator.serviceWorker.getRegistration()
    await registration?.update()

    if ($pwa?.needRefresh) updateCheckStatus.value = 'idle'
    else showUpdateStatus('current')
  } catch {
    showUpdateStatus('error')
  }
}

function showUpdateStatus(
  status: Exclude<UpdateCheckStatus, 'idle' | 'checking'>,
) {
  if (updateStatusTimer) window.clearTimeout(updateStatusTimer)

  updateCheckStatus.value = status
  updateStatusTimer = window.setTimeout(() => {
    updateCheckStatus.value = 'idle'
  }, 3000)
}

onMounted(() => {
  window.addEventListener('scroll', handleHeaderScroll, true)

  if (sessionStorage.getItem('pwa-update-just-installed') !== 'true') return

  sessionStorage.removeItem('pwa-update-just-installed')
  showUpdateStatus('updated')
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleHeaderScroll, true)
  if (updateStatusTimer) window.clearTimeout(updateStatusTimer)
})

function handleHeaderScroll(event: Event) {
  const target = event.target
  const scrollTop =
    target instanceof HTMLElement
      ? target.scrollTop
      : (document.scrollingElement?.scrollTop ?? window.scrollY)

  isScrolled.value = scrollTop > 8
}

function handleClassChange(event: Event) {
  selectClass((event.target as HTMLSelectElement).value)
}

watch(
  () => route.fullPath,
  () => {
    menuOpen.value = false
    isScrolled.value = false
  },
)
</script>

<template>
  <div
    v-if="isVisible"
    class="mobile-app-header"
    :class="{
      'mobile-app-header--solid': isScrolled || menuOpen,
    }"
  >
    <div class="mobile-app-header__bar">
      <div
        class="hidden md:flex mobile-app-header__identity-area"
        :class="{ '!flex': leadingAction }"
      >
        <NuxtLink
          v-if="leadingAction"
          :to="leadingAction.to"
          class="mobile-app-header__button mobile-app-header__leading"
          :aria-label="leadingAction.label"
          :title="leadingAction.label"
        >
          <UIcon :name="leadingAction.icon" class="size-6" />
        </NuxtLink>
        <NuxtLink
          v-if="showSchoolIdentity"
          :to="schoolHome"
          class="mobile-app-header__school-identity"
          :aria-label="`${schoolName} home`"
        >
          <span class="mobile-app-header__school-mark">
            <img src="/img/mascot/mascot-pointing.webp" alt="" />
          </span>
          <span class="mobile-app-header__school-name">{{ schoolName }}</span>
        </NuxtLink>
      </div>

      <div
        class="mobile-app-header__actions flex justify-end md:justify-between"
      >
        <label
          v-if="showClassSwitcher"
          class="mobile-app-header__class-switcher"
        >
          <span class="sr-only">Switch class</span>
          <UIcon name="i-lucide-school" class="mobile-app-header__class-icon" />
          <select
            :value="selectedClassId"
            aria-label="Switch class"
            @change="handleClassChange"
          >
            <option
              v-for="teacherClass in teacherClasses"
              :key="teacherClass.id"
              :value="teacherClass.id"
            >
              {{ teacherClass.name }} · {{ teacherClass.studentCount }} students
            </option>
          </select>
          <UIcon
            name="i-lucide-chevrons-up-down"
            class="mobile-app-header__class-chevron"
          />
        </label>
        <div class="flex gap-4">
          <div
            v-if="user"
            class="hidden md:flex mobile-app-header__user !py-3 !px-6"
            :title="userDisplayName"
          >
            <span class="mobile-app-header__user-initial">{{
              userInitial
            }}</span>
            <span class="mobile-app-header__user-name">{{
              userDisplayName
            }}</span>
          </div>

          <button
            type="button"
            data-adventure-music-toggle
            class="mobile-app-header__button"
            :aria-label="
              isSoundEnabled ? 'Turn all sounds off' : 'Turn all sounds on'
            "
            :aria-pressed="isSoundEnabled"
            :title="
              isSoundEnabled ? 'Turn all sounds off' : 'Turn all sounds on'
            "
            @click="toggleSound"
          >
            <UIcon
              :name="isSoundEnabled ? 'i-lucide-volume-2' : 'i-lucide-volume-x'"
              class="size-5"
            />
          </button>
          <button
            type="button"
            class="mobile-app-header__button"
            :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
            :aria-expanded="menuOpen"
            aria-controls="mobile-app-navigation"
            @click="menuOpen = !menuOpen"
          >
            <UIcon
              :name="menuOpen ? 'i-lucide-x' : 'i-lucide-menu'"
              class="size-6"
            />
          </button>
        </div>
      </div>
    </div>

    <Transition name="mobile-menu-fade">
      <button
        v-if="menuOpen"
        type="button"
        class="mobile-app-header__scrim"
        aria-label="Close menu"
        @click="menuOpen = false"
      />
    </Transition>
    <Transition name="mobile-menu-slide">
      <nav
        v-if="menuOpen"
        id="mobile-app-navigation"
        class="mobile-app-header__drawer"
        aria-label="App navigation"
      >
        <section v-if="appItems.length">
          <p class="mobile-app-header__section-title">Explore</p>
          <NuxtLink
            v-for="item in appItems"
            :key="item.to"
            :to="item.to"
            class="mobile-app-header__link"
            :class="{ 'mobile-app-header__link--active': isActive(item) }"
            @click="menuOpen = false"
          >
            <UIcon :name="item.icon" class="size-5" />{{ item.label }}
          </NuxtLink>
        </section>

        <section v-if="canManageProposals" class="mobile-app-header__section">
          <p class="mobile-app-header__section-title">System Admin</p>
          <label
            v-if="isSystemAdmin"
            class="mobile-app-header__class-switcher mobile-app-header__school-switcher"
          >
            <span class="sr-only">Select school</span>
            <UIcon
              name="i-lucide-school"
              class="mobile-app-header__class-icon"
            />
            <select
              :value="currentRole.schoolId || ''"
              aria-label="Select school"
              :disabled="!accessibleSchools.length"
              @change="handleSchoolChange"
            >
              <option value="" disabled>
                {{
                  accessibleSchools.length ? 'Select school' : 'No schools yet'
                }}
              </option>
              <option
                v-for="school in accessibleSchools"
                :key="school.schoolId"
                :value="school.schoolId"
              >
                {{ school.schoolName }}
              </option>
            </select>
            <UIcon
              name="i-lucide-chevrons-up-down"
              class="mobile-app-header__class-chevron"
            />
          </label>
          <NuxtLink
            to="/dashboard/proposals"
            class="mobile-app-header__link"
            @click="menuOpen = false"
          >
            <UIcon name="i-lucide-file-lock-2" class="size-5" />
            School proposals
          </NuxtLink>
        </section>
        <section
          v-if="dashboardItems.length"
          class="mobile-app-header__section"
        >
          <p class="mobile-app-header__section-title">{{ dashboardTitle }}</p>
          <NuxtLink
            v-for="item in dashboardItems"
            :key="item.to"
            :to="item.to"
            class="mobile-app-header__link"
            :class="{ 'mobile-app-header__link--active': isActive(item) }"
            @click="menuOpen = false"
          >
            <UIcon :name="item.icon" class="size-5" />{{ item.label }}
          </NuxtLink>
        </section>

        <button
          v-if="user"
          type="button"
          class="mobile-app-header__link mobile-app-header__sign-out"
          @click="signOut"
        >
          <UIcon name="i-lucide-log-out" class="size-5" />Sign out
        </button>

        <button
          type="button"
          class="mobile-app-header__version"
          :disabled="updateCheckStatus === 'checking'"
          :aria-label="
            $pwa?.needRefresh ? 'Update WLearn now' : 'Check for updates'
          "
          @click="checkForUpdate"
        >
          <span>Version {{ appVersion }}</span>
          <UIcon
            :name="
              updateCheckStatus === 'checking'
                ? 'i-lucide-loader-circle'
                : $pwa?.needRefresh
                  ? 'i-lucide-download'
                  : 'i-lucide-refresh-cw'
            "
            class="size-3.5"
            :class="{ 'animate-spin': updateCheckStatus === 'checking' }"
          />
          <span
            v-if="updateCheckStatus !== 'idle'"
            class="mobile-app-header__update-status"
            aria-live="polite"
          >
            <template v-if="updateCheckStatus === 'checking'"
              >Checking…</template
            >
            <template v-else-if="updateCheckStatus === 'current'"
              >Already up to date</template
            >
            <template v-else-if="updateCheckStatus === 'updated'"
              >New version installed</template
            >
            <template v-else>Failed</template>
          </span>
        </button>
      </nav>
    </Transition>
  </div>
</template>

<style scoped>
.mobile-app-header {
  position: fixed;
  inset: 0 0 auto;
  z-index: 100;
  pointer-events: none;
}
.mobile-app-header__bar {
  display: grid;
  background-color: transparent;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  min-height: calc(4.15rem + env(safe-area-inset-top));
  padding: max(0.75rem, env(safe-area-inset-top)) 1rem 0.75rem;
  pointer-events: auto;
  transition:
    background-color 180ms ease,
    box-shadow 180ms ease;
}
.mobile-app-header--solid .mobile-app-header__bar {
  background-color: #ffffff;
  box-shadow: 0 0.35rem 1rem rgb(7 78 145 / 10%);
}
.mobile-app-header__identity-area {
  min-width: 0;
  align-items: center;
  gap: 0.65rem;
}
.mobile-app-header__school-identity {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.55rem;
  color: #074e91;
  font-family: Fredoka, Montserrat, sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.05;
  text-decoration: none;
}
.mobile-app-header__school-identity:focus-visible {
  outline: 0.22rem solid #ffcf3f;
  outline-offset: 0.18rem;
  border-radius: 0.8rem;
}
.mobile-app-header__school-mark {
  display: grid;
  width: 3.1rem;
  height: 3.1rem;
  flex: 0 0 auto;
  place-items: center;
  overflow: hidden;
  border: 2px solid #d4e9ff;
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 0.25rem 0.8rem rgb(0 72 145 / 10%);
}
.mobile-app-header__school-mark img {
  width: 3.85rem;
  max-width: none;
  height: 3.85rem;
  object-fit: contain;
  object-position: center;
}
.mobile-app-header__school-name {
  max-width: min(18rem, 24vw);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mobile-app-header__actions {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.mobile-app-header__class-switcher {
  position: relative;
  display: block;
  width: clamp(13rem, 28vw, 17.5rem);
  height: 3.65rem;
  color: #075cae;
}
.mobile-app-header__class-switcher select {
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0 2.6rem 0 2.8rem;
  border: 2px solid #9dccff;
  border-radius: 1.15rem;
  outline: none;
  color: #074e91;
  background: rgb(255 255 255 / 96%);
  box-shadow: 0 0.35rem 1rem rgb(0 72 145 / 12%);
  font-family: Fredoka, Montserrat, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
  appearance: none;
}
.mobile-app-header__class-switcher select:focus-visible {
  border-color: #1678e5;
  outline: 0.22rem solid #ffcf3f;
  outline-offset: 0.16rem;
}
.mobile-app-header__class-icon,
.mobile-app-header__class-chevron {
  position: absolute;
  top: 50%;
  z-index: 1;
  width: 1.2rem;
  height: 1.2rem;
  pointer-events: none;
  transform: translateY(-50%);
}
.mobile-app-header__class-icon {
  left: 0.95rem;
}
.mobile-app-header__class-chevron {
  right: 0.9rem;
  width: 1rem;
  height: 1rem;
}
.mobile-app-header__button {
  display: grid;
  width: 3.65rem;
  height: 3.65rem;
  place-items: center;
  border: 2px solid rgb(255 255 255 / 90%);
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(180deg, #259dff, #0768df);
  box-shadow:
    0 0.35rem 0 #0752b9,
    0 0.75rem 1.2rem rgb(0 48 137 / 25%);
  text-decoration: none;
  transition:
    transform 150ms ease,
    background-color 150ms ease;
}
.mobile-app-header__button:hover {
  background: linear-gradient(180deg, #4eb5ff, #1680e8);
  transform: translateY(-0.1rem);
}
.mobile-app-header__button :deep(svg) {
  width: 1.55rem;
  height: 1.55rem;
}
@media (min-width: 1024px) {
  .mobile-app-header__bar {
    padding-right: 1.5rem;
    padding-left: max(1.5rem, calc((100vw - 1500px) / 2 + 3rem));
  }
  .mobile-app-header__button {
    width: 3.85rem;
    height: 3.85rem;
  }
  .mobile-app-header__class-switcher {
    height: 3.85rem;
  }
  .mobile-app-header__drawer {
    top: 5.75rem;
    right: 1.5rem;
  }
}
.mobile-app-header__user {
  max-width: 12rem;
  min-width: 0;
  align-items: center;
  gap: 0.45rem;
  padding: 0.38rem 0.7rem 0.38rem 0.4rem;
  border: 1px solid #d4e9ff;
  border-radius: 999px;
  color: #074e91;
  background: rgb(255 255 255 / 88%);
  box-shadow: 0 0.25rem 0.8rem rgb(0 72 145 / 10%);
  font-family: Fredoka, Montserrat, sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
}
.mobile-app-header__user-initial {
  display: grid;
  width: 2.05rem;
  height: 2.05rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 999px;
  color: #fff;
  background: #9044f5;
}
.mobile-app-header__user-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mobile-app-header__button:focus-visible,
.mobile-app-header__link:focus-visible {
  outline: 0.22rem solid #ffcf3f;
  outline-offset: 0.16rem;
}
.mobile-app-header__leading {
  justify-self: start;
}
.mobile-app-header__scrim {
  position: fixed;
  inset: 0;
  z-index: -1;
  width: 100%;
  height: 100dvh;
  border: 0;
  background: rgb(13 38 68 / 28%);
  backdrop-filter: blur(0.15rem);
  pointer-events: auto;
}
.mobile-app-header__drawer {
  position: fixed;
  top: calc(env(safe-area-inset-top) + 5.5rem);
  right: 0.5rem;
  z-index: 1;
  display: grid;
  gap: 1.75rem;
  width: min(32rem, calc(100vw - 1rem));
  max-height: calc(100dvh - env(safe-area-inset-top) - 6.25rem);
  overflow-y: auto;
  padding: 2rem;
  border: 2px solid rgb(255 255 255 / 90%);
  border-radius: 1.75rem;
  background: rgb(255 255 255 / 94%);
  box-shadow: 0 1.1rem 2.5rem rgb(7 51 91 / 24%);
  backdrop-filter: blur(1rem);
  pointer-events: auto;
}
.mobile-app-header__section {
  padding-top: 1.15rem;
  border-top: 1px solid #d7e9f8;
}
.mobile-app-header__section-title {
  margin: 0 0 0.8rem;
  color: #3970a0;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}
.mobile-app-header__link {
  display: flex;
  align-items: center;
  gap: 1.15rem;
  min-height: 4.4rem;
  padding: 1rem 1.2rem;
  border-radius: 1.15rem;
  color: #315e87;
  font-family: Fredoka, Montserrat, sans-serif;
  font-size: 1.45rem;
  font-weight: 700;
  text-align: left;
  text-decoration: none;
}
.mobile-app-header__link :deep(svg) {
  width: 1.65rem;
  height: 1.65rem;
  flex: 0 0 auto;
}
@media (max-width: 380px) {
  .mobile-app-header__bar {
    padding-right: 0.6rem;
    padding-left: 0.6rem;
  }
  .mobile-app-header__actions {
    gap: 0.35rem;
  }
  .mobile-app-header__school-name {
    display: none;
  }
  .mobile-app-header__user-name {
    display: none;
  }
  .mobile-app-header__user {
    padding-right: 0.4rem;
  }
  .mobile-app-header__class-switcher {
    width: 7.75rem;
  }
  .mobile-app-header__class-switcher select {
    padding-right: 1.9rem;
    padding-left: 2.25rem;
    font-size: 0.82rem;
  }
  .mobile-app-header__class-icon {
    left: 0.65rem;
  }
  .mobile-app-header__class-chevron {
    right: 0.55rem;
  }
  .mobile-app-header__drawer {
    padding: 1.6rem;
  }
}
.mobile-app-header__link--active {
  color: #075cae;
  background: #dff1ff;
}
.mobile-app-header__sign-out {
  width: 100%;
  border: 0;
  border-top: 1px solid #d7e9f8;
  border-radius: 0;
  color: #bb3b3b;
  background: transparent;
  cursor: pointer;
}
.mobile-app-header__version {
  display: inline-flex;
  align-items: center;
  justify-self: end;
  gap: 0.35rem;
  margin: -0.75rem 0 0;
  padding: 0.35rem;
  border: 0;
  border-radius: 0.5rem;
  color: #7794ad;
  background: transparent;
  font-size: 0.8rem;
  font-weight: 700;
  text-align: right;
  cursor: pointer;
}
.mobile-app-header__version:hover,
.mobile-app-header__version:focus-visible {
  color: #075cae;
  background: #eef7ff;
}
.mobile-app-header__version:focus-visible {
  outline: 0.18rem solid #ffcf3f;
  outline-offset: 0.12rem;
}
.mobile-app-header__version:disabled {
  cursor: wait;
  opacity: 0.75;
}
.mobile-app-header__update-status {
  color: #075cae;
}
.mobile-menu-fade-enter-active,
.mobile-menu-fade-leave-active,
.mobile-menu-slide-enter-active,
.mobile-menu-slide-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}
.mobile-menu-fade-enter-from,
.mobile-menu-fade-leave-to {
  opacity: 0;
}
.mobile-menu-slide-enter-from,
.mobile-menu-slide-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem) scale(0.98);
}
</style>

<style scoped>
.mobile-app-header__school-switcher {
  width: 100%;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
}
</style>
